import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
// import employeedata from '../../assets/localdb/employeedata';
// import { addDriverDeposit } from "../../assets/localdb/depositdata";
import { toast } from "react-toastify";
import { getAllDrivers } from "../../api/driverApi";
import {
  addDriverDeposit,
  getDriverDepositById,
  updateDriverDeposit,
} from "../../api/driverDepositApi";
import usePath from "../../hooks/usePath";
import useFileUploader from "../../hooks/useFileUploader";
import SafeImage from "../../components/common/SafeImage";
import { getCurrencySymbolFromPhone } from "../../utils/currencyFromPhone";


const toPublicUrl = (path) => {
  if (!path) return "";

  // local preview (FileReader / createObjectURL)
  if (path.startsWith("data:") || path.startsWith("blob:")) return path;

  //  already full url
  if (path.startsWith("http")) return path;

  // backend relative path
  return `https://api.gohomeexpress.com/storage/${path}`;
};


/**
 * AddDriverDepositCash Component
 * Manages driver petty cash deposits - allows adding/editing driver cash allocations
 * Handles form validation, file uploads, and API interactions for driver deposits
 */
const AddDriverDepositCash = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const { uploadImage } = useFileUploader();

  // Form state for driver deposit data including petty cash, balance, expense
  const [formData, setFormData] = useState({
    driver_id: "",
    driver_name: "",
    petty_cash: "",
    balance: "",
    expense: "",
    deposit_date: new Date().toISOString().slice(0, 10),
    receipt_number: "",
    receipt_image: "",
    notes: "",
  });

  const [drivers, setDrivers] = useState([]);
  const [loadingDrivers, setLoadingDrivers] = useState(true);
  const [loadingDeposit, setLoadingDeposit] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [receiptFile, setReceiptFile] = useState(null);
const [currencySymbol, setCurrencySymbol] = useState(" ");
  // Fetch all available drivers from backend API
  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        const data = await getAllDrivers();
        setDrivers(data);
      } catch (error) {
        toast.error("Failed to load drivers");
      } finally {
        setLoadingDrivers(false);
      }
    };
    fetchDrivers();
  }, []);

  // Load existing deposit data when editing mode is active
  useEffect(() => {
    if (isEdit) {
      const fetchDeposit = async () => {
        try {
          const deposit = await getDriverDepositById(id);
          setFormData({
            ...deposit,
            petty_cash: deposit.petty_cash || "",
            expense: deposit.expense || "",
            balance: deposit.balance || "",
            deposit_date: deposit.deposit_date
              ? deposit.deposit_date.slice(0, 10)
              : new Date().toISOString().slice(0, 10),
            receipt_number: deposit.receipt_number || "",
            receipt_image: deposit.receipt_image || "",
            notes: deposit.notes || "",
            driver_id: deposit.driver_id ? deposit.driver_id.toString() : "",
            driver_name: deposit.driver_name || "",
          });

          if (deposit?.receipt_image) {
  setImagePreview(deposit.receipt_image); // ✅ EDIT MODE image preview
}

        } catch (error) {
          toast.error("Failed to load deposit");
        } finally {
          setLoadingDeposit(false);
        }
      };
      fetchDeposit();
    }
  }, [isEdit, id]);

  

  // ✅ EDIT MODE: set currency symbol automatically (NO new function)
useEffect(() => {
  if (!isEdit) return;
  if (!formData.driver_id) return;
  if (drivers.length === 0) return;

  const driver = drivers.find(
    (d) => d.driver_id.toString() === formData.driver_id.toString()
  );

  const phone = driver?.user?.phone_no || driver?.phone_no;
  setCurrencySymbol(getCurrencySymbolFromPhone(phone));
}, [isEdit, formData.driver_id, drivers]);


  // Auto-calculate balance based on petty cash and expense values
  useEffect(() => {
    const pettyCash = parseFloat(formData.petty_cash) || 0;
    const expense = parseFloat(formData.expense) || 0;
    const balance = pettyCash - expense;
    setFormData((prev) => ({
      ...prev,
      balance: balance.toString(),
    }));
  }, [formData.petty_cash, formData.expense]);

  // Handle form input changes and update state
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Update driver selection and auto-populate driver name
  const handleDriverChange = (e) => {
    const driver_id = e.target.value;
    const selectedDriver = drivers.find(
      (d) => d.driver_id.toString() === driver_id,
    );
    const phone = selectedDriver?.user?.phone_no || selectedDriver?.phone_no
    setFormData((prev) => ({
      ...prev,
      driver_id,
      driver_name: selectedDriver ? selectedDriver.user.name : "",
    }));
    // ✅ phone → currency symbol
  setCurrencySymbol(getCurrencySymbolFromPhone(phone));
  };

  // Handle receipt image file upload
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setReceiptFile(file);
      setFormData((prev) => ({
        ...prev,
        receipt_image: usePath("/" + file.name), // for display only
      }));
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
      console.log("Selected image file:", file);
    } else {
      setImagePreview(null);
      if (file) {
        alert("Please select a valid image file (jpeg, png, jpg, gif).");
      }
    }
  };

  // Submit form data to create or update driver deposit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const form = new FormData();

      // Prepare form data with expense always set to 0
      const submitData = {
        ...formData,
        expense: "0", // Always set expense to 0 since field is hidden
      };

      if (receiptFile) {
        submitData.receipt_image = usePath(
          await uploadImage(receiptFile, "receipts", receiptFile.name),
        );
      }

      Object.entries(submitData).forEach(([key, value]) => {
        form.append(key, value);
      });

      if (isEdit) {
        await updateDriverDeposit(id, form);
        toast.success("Driver deposit updated successfully!");
      } else {
        await addDriverDeposit(form);
        toast.success("Driver deposit added successfully!");
      }
      navigate("/driverdepositcash");
    } catch (error) {
      toast.error("Failed to save driver deposit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingDeposit) {
    return (
      <>
        <div
          className="skeleton-loader"
          style={{ width: "200px", height: "24px" }}
        ></div>
        <div className="row g-3">
          <div className="col-md-12">
            <div
              className="skeleton-loader"
              style={{ width: "100%", height: "80px" }}
            ></div>
          </div>
          <div className="col-md-6">
            <div
              className="skeleton-loader"
              style={{ width: "100%", height: "80px" }}
            ></div>
          </div>
          <div className="col-md-6">
            <div
              className="skeleton-loader"
              style={{ width: "100%", height: "80px" }}
            ></div>
          </div>
          <div className="col-md-6">
            <div
              className="skeleton-loader"
              style={{ width: "100%", height: "80px" }}
            ></div>
          </div>
          <div className="col-md-6">
            <div
              className="skeleton-loader"
              style={{ width: "100%", height: "80px" }}
            ></div>
          </div>
          <div className="col-md-12">
            <div
              className="skeleton-loader"
              style={{ width: "100%", height: "80px" }}
            ></div>
          </div>
          <div className="col-md-12">
            <div
              className="skeleton-loader"
              style={{ width: "100%", height: "120px" }}
            ></div>
          </div>
          <div className="col-12 d-flex justify-content-end mt-4">
            <div
              className="skeleton-loader"
              style={{ width: "120px", height: "40px" }}
            ></div>
          </div>
        </div>
        <style>
          {`
            .skeleton-loader {
              background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
              background-size: 200% 100%;
              animation: loading 1.5s infinite;
              border-radius: 4px;
            }
            @keyframes loading {
              0% {
                background-position: 200% 0;
              }
              100% {
                background-position: -200% 0;
              }
            }
          `}
        </style>
      </>
    );
  }

  return (
    <>
      <h2 className="mb-3">
        {isEdit ? "Edit Driver Deposit" : "Add Driver Deposit"}
      </h2>
      <form onSubmit={handleSubmit}>
        <div className="row g-3">
          <div className="col-md-12">
            <div className="form-group">
              <label className="form-label">
                Driver <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                name="driver_id"
                value={formData.driver_id}
                onChange={handleDriverChange}
                required
                disabled={loadingDrivers}
              >
                <option value="" disabled>
                  {loadingDrivers ? "Loading drivers..." : "Select a driver"}
                </option>
                {drivers.map((driver) => (
                  <option key={driver.driver_id} value={driver.driver_id}>
                    {driver.user.name} - {driver.vehicle_no}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label className="form-label">
                Petty Cash <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <span className="input-group-text">{currencySymbol}</span>
                <input
                  type="number"
                  className="form-control"
                  name="petty_cash"
                  value={formData.petty_cash}
                  onChange={handleChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label className="form-label">Receipt Number</label>
              <input
                type="text"
                className="form-control"
                name="receipt_number"
                value={formData.receipt_number}
                onChange={handleChange}
                placeholder="e.g., RCPT-12345"
              />
            </div>
          </div>
          <div className="col-md-12">
            <div className="form-group">
              <label className="form-label">Receipt Image</label>
              <input
                type="file"
                className="form-control"
                name="receipt_image"
                accept="image/*"
                onChange={handleFileChange}
              />

             {imagePreview && (
  <a
    href={toPublicUrl(imagePreview)}
    target="_blank"
    rel="noreferrer"
  >
    <SafeImage
      src={toPublicUrl(imagePreview)}
      alt="Receipt Image Preview"
      style={{
        width: 120,
        height: 120,
        objectFit: "cover",
        borderRadius: 8,
        marginBottom: 12,
      }}
    />
  </a>
)}


              <div className="form-text">
                Supported formats: JPG, PNG, GIF. Max size: 2MB.
              </div>
              {formData.receipt_image && (
                <small>Selected: {formData.receipt_image}</small>
              )}
            </div>
          </div>
          <div className="col-md-12">
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea
                className="form-control"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Any additional notes"
                rows={3}
              />
            </div>
          </div>
          <div className="col-12 d-flex justify-content-end align-items-center mt-4 gap-2">
            <button
              type="button"
              className="btn btn-link"
              style={{
                color: "#e74c3c",
                fontWeight: 500,
                textDecoration: "none",
                marginRight: "16px",
              }}
              onClick={() => navigate("/driverdepositcash")}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn"
              style={{
                background: "#009e7f",
                color: "#fff",
                fontWeight: 700,
                padding: "10px 40px",
                borderRadius: "6px",
                border: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                letterSpacing: "0.5px",
              }}
              disabled={submitting}
            >
              {submitting
                ? isEdit
                  ? "Updating..."
                  : "Saving..."
                : isEdit
                  ? "Update Deposit"
                  : "Save Deposit"}
            </button>
          </div>
        </div>
      </form>
    </>
  );
};

export default AddDriverDepositCash;
